import * as Sentry from '@sentry/react'
import React, { useEffect, useMemo, useState } from 'react'
import { Empty } from '../../../components'
import { getUserUuidFromLocalStorage } from '../../../utils'
import type { AuditTargetType } from '../changelog'
import { useChangeLogQuery } from '../changelog'
import { useGetDeviceNamesByUuidsQuery } from '../useChangeLogHooks'
import { ChangeLogView } from './ChangeLogView'

const PAGE_SIZE = 10

interface ChangeLogContainerProps {
    targetId: string | undefined
    targetType: AuditTargetType
}

export const ChangeLogContainer = ({ targetId, targetType }: ChangeLogContainerProps) => {
    const [limit, setLimit] = useState(PAGE_SIZE)
    const { data, isFetching, error } = useChangeLogQuery(targetId, targetType)

    useEffect(() => {
        if (!targetId) return
        Sentry.logger.info('changelog.viewed', {
            event: 'changelog_viewed',
            targetId,
            targetType,
            userId: getUserUuidFromLocalStorage()
        })
    }, [targetId, targetType])

    const deviceUuids = useMemo(
        () => [...new Set((data?.change_log ?? []).filter((e) => e.target_type === 'device').map((e) => e.target_id))],
        [data]
    )

    const { data: devicesData } = useGetDeviceNamesByUuidsQuery({ uuids: deviceUuids })

    const deviceNameMap: Record<string, string> = useMemo(
        () => Object.fromEntries((devicesData?.device ?? []).map((d) => [d.uuid, d.device_name ?? d.uuid])),
        [devicesData]
    )

    const deviceTypeMap: Record<string, string> = useMemo(
        () =>
            Object.fromEntries(
                (devicesData?.device ?? [])
                    .filter((d): d is typeof d & { type: string } => Boolean(d.type))
                    .map((d) => [d.uuid, d.type])
            ),
        [devicesData]
    )

    if (!targetId) return <Empty title="Select a device first!" />
    if (error) return <div>Error loading change log: {(error as Error).message}</div>

    const entries = [...(data?.change_log ?? [])].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )
    const visible = entries.slice(0, limit)
    const hasMore = entries.length > limit

    return (
        <ChangeLogView
            entries={visible}
            isFetching={isFetching}
            hasMore={hasMore}
            onShowMore={() => setLimit((l) => l + PAGE_SIZE)}
            deviceNameMap={deviceNameMap}
            deviceTypeMap={deviceTypeMap}
        />
    )
}
